<script setup lang="ts">
import { useWindowFocus } from "@vueuse/core";
import { computed, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { VCol } from "vuetify/lib/components/index.mjs";
import { rootRoutesNames } from "@/router/routes";
import UiDialog from "@/shared/ui/UiDialog.vue";
import { useAppStore } from "@/store/app";

const store = useAppStore();
const router = useRouter();
const focused = useWindowFocus();

const route = useRoute();

const routeClientId = computed(() => route.params.clientId);
const storageClientID = ref("");
const isDialogVisible = ref(false);

const getLocalStorageClientID = () => {
  const user = localStorage.getItem("user");
  if (user) {
    try {
      return JSON.parse(user).clientID;
    } catch {
      return;
    }
  }
};

const activeClientName = computed(() => {
  return (
    store.userInfo?.clients.find(
      (client) => client.clientID === storageClientID.value,
    )?.name || ""
  );
});

const activeClientAvatar = computed(() => {
  return (
    store.userInfo?.clients.find(
      (client) => client.clientID === storageClientID.value,
    )?.client_avatar || ""
  );
});

const routeClientName = computed(() => {
  return (
    store.userInfo?.clients.find(
      (client) => client.clientID === routeClientId.value,
    )?.name || ""
  );
});

const handleSwitchBack = async () => {
  await store.setClientID(routeClientId.value as string);
  isDialogVisible.value = false;
};

watch(
  () => [focused.value, routeClientId.value],
  () => {
    if (!focused.value) {
      isDialogVisible.value = false;
      return;
    }

    storageClientID.value = getLocalStorageClientID() || "";
    if (
      storageClientID.value &&
      routeClientId.value &&
      storageClientID.value !== routeClientId.value &&
      routeClientName.value
    ) {
      isDialogVisible.value = true;
    } else {
      isDialogVisible.value = false;
    }
  },
  { immediate: true, deep: true },
);
</script>
<template>
  <UiDialog
    v-model:dialogVisible="isDialogVisible"
    title="You are already logged to a different ECCA workspace"
    persistent
    :closeIcon="false"
    icon="$TriangleExclamation"
    iconColor="warning"
  >
    <VRow class="text-center">
      <VCol>
        <p>Please note that active workspace is</p>
        <VAvatar :image="activeClientAvatar" size="66" class="mt-1" /><br />
        <strong>{{ activeClientName }}</strong>
        <p class="mt-5">
          Do you want to switch back to the
          <strong>{{ routeClientName }}</strong> workspace?
        </p>
      </VCol>
    </VRow>
    <VRow class="mt-5">
      <VCol class="text-center justify-center d-flex" style="gap: 1rem">
        <EccaBtn
          variant="outlined"
          :href="
            router.resolve({
              name: rootRoutesNames.home(),
              params: { clientId: storageClientID },
            }).href
          "
        >
          Cancel
        </EccaBtn>
        <EccaBtn :loading="store.isLoading" @click="handleSwitchBack">
          Switch back to {{ routeClientName }}
        </EccaBtn>
      </VCol>
    </VRow>
  </UiDialog>
</template>
